<template>
  <SafeTeleport to="body">
    <div
      ref="modal"
      class="modal"
      :class="className"
      role="dialog"
      aria-labelledby="exampleModalCenterTitle"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg" :style="modalStyle" role="document">
        <div class="modal-content" data-cy="modal-content">
          <div
            v-if="title"
            class="modal-header px-4 py-3 mb-4 border-b border-solid border-gray-300"
          >
            <div class="d-flex flex-column modal-title-cont">
              <h3
                v-if="title"
                id="exampleModalLongTitle"
                data-cy="modal-title"
                class="modal-title text-lg text-gray-900 font-semibold"
              >
                {{ title }}
              </h3>
            </div>
          </div>
          <button
            v-if="!preventClose"
            type="button"
            data-cy="close"
            data-dismiss="modal"
            class="modal-close-button text-black menu-icon mb-0 bg-gray-100 hover:bg-gray-200 md:bg-transparent md:hover:bg-transparent rounded-full p-4 flex items-center justify-center cursor-pointer"
          >
            <CloseIcon height="13" class="text-gray-500" />
          </button>
          <div
            class="modal-body overflow-auto md:overflow-visible"
            :style="modalStyle"
          >
            <div v-if="$slots['header']" class="mb-2">
              <slot name="header"></slot>
            </div>
            <div class="col-md-12 parsley-validations px-0" :class="className">
              <slot></slot>
            </div>
          </div>
          <slot v-if="!noFooter" name="footer">
            <div class="modal-footer d-flex justify-content-between">
              <div>
                <slot name="delete">
                  <SeraButton
                    v-if="hasDelete"
                    :disabled="disableDelete || disabled"
                    type="danger"
                    data-cy="modal-delete-btn"
                    @click="$emit('delete')"
                  >
                    <span v-if="!deleting">{{ deleteBtnText }}</span>
                    <Spinner v-else :message="deletingText"></Spinner>
                  </SeraButton>
                </slot>
                <SeraButton
                  v-if="cancelButton"
                  type="secondary"
                  text="Cancel"
                  data-dismiss="modal"
                ></SeraButton>
              </div>
              <div>
                <slot v-if="!noSubmit" name="submit">
                  <SeraButton
                    :class="[submitBtnClass]"
                    :disabled="disableSubmit || disabled || saving"
                    data-cy="modal-submit-btn"
                    @click="handleSubmit"
                  >
                    <span v-if="!saving">{{ submitBtnText }}</span>
                    <Spinner v-else :message="savingText"></Spinner>
                  </SeraButton>
                </slot>
              </div>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </SafeTeleport>
</template>

<script>
import debounce from "lodash/debounce";
import { nextTick } from "vue";
import { SafeTeleport } from "vue-safe-teleport";

import SeraButton from "@/components/Button.vue";
import CloseIcon from "@/images/x.svg?component";
import Spinner from "./Spinner.vue";

export default {
  components: {
    SafeTeleport,
    Spinner,
    CloseIcon,
    SeraButton
  },
  props: {
    className: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    open: {
      type: Boolean,
      default: true
    },
    onShow: {
      type: Function,
      default: () => {}
    },
    onHide: {
      type: Function,
      default: () => {}
    },
    saving: {
      type: Boolean,
      default: false
    },
    savingText: {
      type: String,
      default: "Saving..."
    },
    submitBtnText: {
      type: String,
      default: "Save"
    },
    submitBtnClass: {
      type: String,
      default: ""
    },
    deleting: {
      type: Boolean,
      default: false
    },
    deletingText: {
      type: String,
      default: "Deleting..."
    },
    deleteBtnText: {
      type: String,
      default: "Delete"
    },
    noSubmit: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disableDelete: {
      type: Boolean,
      default: false
    },
    disableSubmit: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: null
    },
    noFooter: {
      type: Boolean,
      default: false
    },
    preventClose: {
      type: Boolean,
      default: false
    },
    cancelButton: {
      type: Boolean,
      default: false
    },
    minHeight: {
      type: Number,
      default: 0
    },
    onDelete: {
      type: Function
    }
  },
  emits: ["delete", "show-modal", "hide-modal", "submit"],
  data() {
    return {
      internalId: Date.now() + Math.floor(Math.random() * 10000)
    };
  },
  computed: {
    hasDelete() {
      return this.$props.onDelete;
    },
    cancelButtonText() {
      return this.noSubmit ? "Close" : "Cancel";
    },
    modalStyle() {
      const style = {};
      if (this.width) {
        style["maxWidth"] = `${this.width}px`;
      }
      if (this.minHeight) {
        style["minHeight"] = `${this.minHeight}px`;
      }
      return style;
    }
  },
  watch: {
    open(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          return this.showModal();
        }
        return this.hideModal();
      }
    },
    preventClose(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.toggleCloseSettings();
      }
    }
  },
  mounted() {
    this.initListeners();
    if (this.open) {
      this.showModal();
    }
  },
  beforeUnmount() {
    this.destroyBackShadow();
    // Fixes bug when using modal over modal and the modal scrolling stops working
    const style = $(".modal").attr("style");
    if (!style?.includes("overflow-y")) {
      $(".modal").attr("style", style + "overflow-y: auto;");
    }

    // Using bootstrap modal.hide function does not reliably remove padding-right from the body,
    // so we added this to make sure the padding never gets added after modal is removed
    document.body.style.paddingRight = "";
  },
  methods: {
    initListeners() {
      const vm = this;
      nextTick(() => {
        // Setup listener for shown event
        $(vm.$refs.modal).on("shown.bs.modal", () => {
          vm.onShow();
          vm.$emit("show-modal");
          // auto enable tooltips
          tippy("[data-tippy-content]");
        });
        // Setup listener for hidden event
        $(vm.$refs.modal).on("hidden.bs.modal", () => {
          vm.onHide();
          vm.$emit("hide-modal");
          // Fixes bug when using modal over modal and the modal scrolling stops working
          const style = $(".modal").attr("style");
          if (!style?.includes("overflow-y")) {
            $(".modal").attr("style", style + "overflow-y: auto;");
          }
        });
        vm.toggleCloseSettings();
      });
    },
    toggleCloseSettings() {
      if (this.preventClose) {
        $.fn.modal.prototype.constructor.Constructor.Default.backdrop =
          "static";
        $.fn.modal.prototype.constructor.Constructor.Default.keyboard = false;
      } else {
        $.fn.modal.prototype.constructor.Constructor.Default.backdrop = true;
        $.fn.modal.prototype.constructor.Constructor.Default.keyboard = true;
      }
    },
    showModal: debounce(
      function () {
        const vm = this;
        // Wait for ref to exist
        nextTick(() => {
          // Show modal and add internalId to backshadow
          $(vm.$refs.modal).modal("show");
          $(".modal-backdrop")
            .not(".modal-stack")
            .attr("id", `${this.internalId}`);
        });
      },
      100,
      { leading: true }
    ),
    hideModal() {
      const vm = this;
      // Wait for ref to exist
      nextTick(() => {
        // Hide modal
        $(vm.$refs.modal).modal("hide");
      });
    },
    destroyBackShadow() {
      // Handle lingering modal backdrop on unmounted modals
      // Happens when the modal is unmounted before closing
      if (this.open) {
        const backdrop = $(`#${this.internalId}.modal-backdrop`);
        if (backdrop.length) {
          backdrop.fadeOut(() => {
            backdrop.remove();
          });
        }
        // Re-enable body scroll
        $("body").removeClass("modal-open");
      }
    },
    handleSubmit: debounce(
      function () {
        this.$emit("submit");
      },
      500,
      {
        leading: true
      }
    )
  }
};
</script>

<style lang="scss" scoped>
.modal-header {
  padding: 30px 30px 1rem 30px;
}
.modal-body {
  padding: 0px 30px 1rem 30px;
  /* Add bottom border radius for modals with no footer */
  border-radius: 0 0 4px 4px;
}
.modal-footer {
  padding: 0px 30px 30px 30px;
}
.menu-icon {
  font-size: 1.2rem;
  margin-bottom: 10px;
}
.modal-content {
  position: relative;
}
.modal-close-button {
  position: absolute;
  top: 0.3rem;
  right: 0.75rem;
  z-index: 10;
}
@media screen and (max-width: 852px) {
  .modal-content {
    display: grid;
    grid-template-rows: auto 1fr auto;
    max-height: 100dvh;
  }
}
</style>
